/**
 * System Configuration Types
 * MCP, Mem0, First Message configuration data structures
 */

export interface MCPServerConfig {
  command?: string;
  args?: string[];
  env?: Record<string, string>;
  url?: string;
  transport: "stdio" | "sse" | "streamable-http";
  description?: string;
  status: 0 | 1; // 0 = disabled, 1 = enabled
}

export interface MCPConfig {
  mcpServers: Record<string, MCPServerConfig>;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface MCPServer {
  id: string;
  name: string;
  config: MCPServerConfig;
  status: "connected" | "disconnected" | "error";
  tools_count?: number;
}

export interface Mem0Config {
  user_memory_extraction_prompt?: string;
  agent_memory_extraction_prompt?: string;
  memory_update_prompt?: string;
  user_memory_categories?: string; // JSON string, not array
  agent_memory_categories?: string; // JSON string, not array
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface Mem0Defaults {
  user_memory_extraction_prompt: string;
  agent_memory_extraction_prompt: string;
  memory_update_prompt: string;
  user_memory_categories: string; // JSON string, not array
  agent_memory_categories: string; // JSON string, not array
}

export interface SessionSummarySystemConfig {
  summary_prompt?: string;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface SessionSummarySystemDefaults {
  summary_prompt: string;
  description?: string;
}

export interface HtmlPageSystemConfig {
  prompt_addendum: string;
  csp: string;
  base_css: string;
  max_html_bytes: number;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface HtmlPageSystemDefaults {
  prompt_addendum: string;
  csp: string;
  base_css: string;
  max_html_bytes: number;
}

export interface FirstMessageConfig {
  instruction: string;
  enabled?: boolean;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface AuditLog {
  id: string;
  config_type: string;
  action: string;
  old_version?: number;
  new_version?: number;
  changed_by: string;
  change_reason?: string;
  ip_address?: string;
  created_at: string;
}

export interface AuditLogResponse {
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

// Artifact Types Configuration

/**
 * Field definition for artifact data schema.
 *
 * Each field can be:
 * - Metadata field: no content_types, value stored directly in metadata.json
 * - Media field: has content_types, value is file path, actual file in parallel folder
 */
export interface ArtifactFieldDef {
  /** Field key (required, e.g., "image", "phrase", "audio") */
  key: string;
  /** Display label in English */
  label?: string;
  /** Display label in Chinese */
  label_zh?: string;
  /** Whether this field is required */
  required?: boolean;
  /** Field description */
  description?: string;
  /**
   * Allowed content types for this field (MIME types).
   * If specified, this is a media field - value will be file path.
   * If not specified, this is a metadata field - value stored directly.
   * Examples: ["image/png", "image/jpeg"] or ["audio/mpeg", "audio/wav"]
   */
  content_types?: string[];
}

/**
 * Check if a field is a media field (has content_types)
 */
export function isMediaField(field: ArtifactFieldDef): boolean {
  return Boolean(field.content_types && field.content_types.length > 0);
}

/**
 * Get the media category from content types (image, audio, video, document)
 */
export function getMediaCategory(contentTypes: string[]): string | null {
  if (!contentTypes || contentTypes.length === 0) return null;
  const first = contentTypes[0];
  if (first.startsWith("image/")) return "image";
  if (first.startsWith("audio/")) return "audio";
  if (first.startsWith("video/")) return "video";
  if (first.startsWith("text/") || first.startsWith("application/"))
    return "document";
  return null;
}

export type LifecycleMode = "rotation" | "scheduled";
export type ArtifactSortBy = "created_at" | "valid_from";

export interface ArtifactTypeConfig {
  name: string;
  name_zh?: string;
  description?: string;
  description_zh?: string;
  rotation?: "daily" | "weekly" | "monthly" | null;
  max_stored?: number | null;
  auto_cleanup?: boolean;
  icon?: string;
  lifecycle_mode?: LifecycleMode;
  sort_by?: ArtifactSortBy;
  retention_days_after_valid?: number;
  /**
   * Data schema defines what fields this artifact type requires.
   * Each field has a key and optional content_types.
   * - With content_types: media field (stores file path)
   * - Without content_types: metadata field (stores value directly)
   */
  data_schema?: ArtifactFieldDef[];
}

export interface ArtifactTypesConfig {
  artifact_types: Record<string, ArtifactTypeConfig>;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface ArtifactType {
  id: string;
  config: ArtifactTypeConfig;
}

// Tool Enhancement Configuration
export interface ToolEnhancementToolConfig {
  display_name_cn?: string;
  display_name_en?: string;
  description_cn?: string;
  description_en?: string;
  icon?: string;
  category?: string;
  enabled?: boolean;
  display_order?: number;
}

export interface ToolEnhancementCategoryConfig {
  display_name_cn?: string;
  display_name_en?: string;
  icon?: string;
  display_order?: number;
}

export interface ToolEnhancementConfig {
  tools: Record<string, ToolEnhancementToolConfig>;
  categories: Record<string, ToolEnhancementCategoryConfig>;
  default_language: "cn" | "en";
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

// System Prompt Template Configuration
export interface SystemPromptTemplateConfig {
  name: string;
  name_zh: string;
  description?: string;
  description_zh?: string;
  system_prompt_zh: string;
  system_prompt_en: string;
  display_order: number;
  is_default: boolean;
  category?: string;
}

export interface SystemPromptTemplate {
  key: string;
  name: string;
  name_zh: string;
  description?: string;
  description_zh?: string;
  system_prompt_zh: string;
  system_prompt_en: string;
  display_order: number;
  is_default: boolean;
  category?: string;
}

export interface SystemPromptTemplateSimple {
  key: string;
  name: string;
  name_zh: string;
  description?: string;
  description_zh?: string;
  category?: string;
}

export interface SystemPromptTemplatesConfig {
  templates: Record<string, SystemPromptTemplateConfig>;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface SystemPromptTemplateCreate {
  key: string;
  name: string;
  name_zh: string;
  description?: string;
  description_zh?: string;
  system_prompt_zh: string;
  system_prompt_en: string;
  display_order?: number;
  category?: string;
}

export interface SystemPromptTemplateUpdate {
  name?: string;
  name_zh?: string;
  description?: string;
  description_zh?: string;
  system_prompt_zh?: string;
  system_prompt_en?: string;
  display_order?: number;
  category?: string;
}

// Tool Combo Configuration
export interface ToolComboTool {
  tool_name: string;
  order: number;
  usage_instruction?: string;
}

export interface ToolComboTemplate {
  id: string;
  name_cn?: string;
  name_en?: string;
  description_cn?: string;
  description_en?: string;
  tools: ToolComboTool[];
  guidance?: string;
  enabled?: boolean;
}

export interface ToolComboConfig {
  templates: Record<string, ToolComboTemplate>;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface ToolComboTemplateSummary {
  id: string;
  name: string;
  description?: string;
  tool_count: number;
  enabled: boolean;
}

export interface ToolComboTemplateUsage {
  template_id: string;
  usage_count: number;
  agent_types: string[];
}

// Model Defaults Configuration
export interface ModelCategoryConfig {
  model_id: string;
  temperature?: number;
  top_p?: number;
  max_tokens?: number;
}

export interface ModelCategoryWithQuickConfig {
  model_id: string;
  quick_model_id: string;
  temperature?: number;
  top_p?: number;
  max_tokens?: number;
}

export interface RerankingCategoryConfig {
  model_id: string;
  enabled?: boolean;
  top_k?: number;
}

export interface TaskCategoryConfig {
  model_id: string;
  temperature?: number;
  max_tokens?: number;
}

export interface ModelDefaultsConfig {
  _meta?: { global: string[] };
  chat: ModelCategoryWithQuickConfig;
  vision: ModelCategoryWithQuickConfig;
  memory: ModelCategoryConfig;
  classification: ModelCategoryConfig;
  reasoning: ModelCategoryConfig;
  embedding: { model_id: string };
  reranking: RerankingCategoryConfig;
  stt: { model_id: string };
  tts: { model_id: string };
  task: TaskCategoryConfig;
  image?: {
    model_id: string;
    quality_model_id?: string;
    image_size?: string;
    default_aspect_ratio?: string;
  };
  prompt_rewrite?: ModelCategoryConfig;
  mbti?: ModelCategoryConfig;
  // Metadata
  version?: number;
  updated_by?: string;
  updated_at?: string;
}

export interface AudioSettings {
  tts: {
    provider: string;
    default_speed: number;
    streaming_enabled: boolean;
    preamble_trim_ms: number;
    providers: Record<string, Record<string, unknown>>;
  };
  stt: {
    provider: string;
    model: string;
    fallback_model: string;
    hotwords_enabled: boolean;
    providers: Record<string, Record<string, unknown>>;
  };
  voice_call: {
    enabled: boolean;
    provider: string;
    model: string;
    fallback_model: string | null;
    interrupt_enabled: boolean;
    vad_mode: string;
    providers: Record<string, Record<string, unknown>>;
  };
}

export interface AudioProviderMeta {
  provider_id: string;
  display_name: string;
  voices: { id: string; name: string }[];
  supports_streaming: boolean;
  speed_range: [number, number];
  extra_config_schema: Record<string, unknown> | null;
}

export interface VoiceCallProviderMeta {
  provider_id: string;
  display_name: string;
  default_model: string;
  models: {
    id: string;
    name: string;
    description: string;
    supports_interrupt: boolean;
    supports_vad: boolean;
    modalities: string[];
  }[];
}
